import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCheck, faChevronDown, faGear, faLanguage } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { LANGUAGE_NAME_MAP } from "./types";
import { LanguageFlag } from "./LanguageFlag";

const MENU_WIDTH = 256;
/** Ideal menu height (search box + list + optional footer) before it must scroll. */
const PREFERRED_MAX_HEIGHT = 360;
/** Gap kept between the menu and the viewport edge / the trigger. */
const MENU_MARGIN = 8;
/** Smallest height we let the menu shrink to before it just scrolls internally. */
const MIN_MENU_HEIGHT = 160;

interface LanguageSelectDropdownProps {
	language: string;
	onSelect: (language: string) => void;
	onOpenRules: () => void;
	disabled?: boolean;
	/**
	 * Compact (DetailView "Chat" tab / extension panel): shortens the label
	 * ("Auto" / language code instead of "Auto-detect" / full name) and wraps the
	 * pill in a hover tooltip so it fits the narrow control row.
	 */
	compact?: boolean;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

interface LanguageOption {
	id: string;
	title: string;
}

/**
 * LanguageSelectDropdown — the language pill trigger in the GenerateBar control
 * row (PREC-3570: replaces the old circular flag/globe button). It shows a
 * "languages" icon + "Auto-detect" (or the selected language's flag + name) and
 * opens a portal-rendered language menu directly (no modal / apply step). When
 * "auto" is the current selection, a pinned footer row opens the language
 * auto-detect rules modal via `onOpenRules`.
 */
export const LanguageSelectDropdown: FunctionComponent<LanguageSelectDropdownProps> = ({
	language,
	onSelect,
	onOpenRules,
	disabled = false,
	compact = false,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [showDropdown, setShowDropdown] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [portalPosition, setPortalPosition] = useState<{
		top?: number;
		bottom?: number;
		left: number;
		maxHeight: number;
	} | null>(null);

	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const options = useMemo<LanguageOption[]>(
		() => [
			{ id: "auto", title: t("messages.generateBar.languageSelect.autoDetect") },
			...Object.entries(LANGUAGE_NAME_MAP).map(([code, name]) => ({ id: code, title: name })),
		],
		[t]
	);

	const filteredOptions = useMemo(() => {
		const query = searchValue.trim().toLowerCase();

		if (!query) {
			return options;
		}

		return options.filter(
			(option) => option.title.toLowerCase().includes(query) || option.id.includes(query)
		);
	}, [options, searchValue]);

	// Close when clicking outside the trigger and the portal menu.
	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideTrigger = triggerRef.current && triggerRef.current.contains(target as Node);
			const insideMenu = menuRef.current && menuRef.current.contains(target as Node);

			if (!insideTrigger && !insideMenu && showDropdown) {
				setShowDropdown(false);
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [showDropdown]);

	// Keep the portal menu anchored to the trigger.
	useEffect(() => {
		if (!showDropdown) {
			return;
		}

		const updatePosition = () => {
			if (!triggerRef.current) {
				return;
			}

			const rect = triggerRef.current.getBoundingClientRect();
			const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8));

			// Flip above the trigger when there isn't room below and there's more room
			// above; either way, cap the height to the available space so the option
			// list scrolls internally instead of spilling off the viewport edge.
			const spaceBelow = window.innerHeight - rect.bottom - MENU_MARGIN;
			const spaceAbove = rect.top - MENU_MARGIN;
			const openUp = spaceBelow < PREFERRED_MAX_HEIGHT && spaceAbove > spaceBelow;
			const available = Math.max(MIN_MENU_HEIGHT, openUp ? spaceAbove : spaceBelow);
			const maxHeight = Math.min(PREFERRED_MAX_HEIGHT, available);

			setPortalPosition(
				openUp
					? { bottom: window.innerHeight - rect.top + 4, left, maxHeight }
					: { top: rect.bottom + 4, left, maxHeight }
			);
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [showDropdown]);

	const closeDropdown = () => {
		setShowDropdown(false);
		setSearchValue("");
	};

	const handleSelectLanguage = (code: string) => {
		onSelect(code);
		closeDropdown();
	};

	const isAuto = language === "auto";
	// `triggerTitle` stays the full name / code (used for the native title + flag
	// SVG title); tests locate the trigger via this value, so keep it stable.
	const triggerTitle = isAuto
		? t("messages.generateBar.languageSelect.autoDetect")
		: language.toUpperCase();
	// Visible pill label: shorter in compact so it fits the narrow control row.
	const triggerLabel = isAuto
		? compact
			? t("messages.generateBar.languageSelect.autoShort")
			: t("messages.generateBar.languageSelect.autoDetect")
		: compact
			? language.toUpperCase()
			: LANGUAGE_NAME_MAP[language] || language.toUpperCase();

	const trigger = (
		<button
			ref={triggerRef}
			className={classNames(
				"input-styles w-max inline-flex items-center gap-2 pl-3 pr-2.5 hover:shadow-sm cursor-pointer",
				"!py-1 !h-[32px] !rounded-[10px] text-xs font-semibold"
			)}
			data-test="language-select-trigger"
			data-testid="language-select-trigger"
			disabled={disabled}
			title={compact ? undefined : triggerTitle}
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				setShowDropdown((prev) => !prev);
			}}
		>
			{isAuto ? (
				<Icon className="w-3.5 h-3.5 text-text-meta shrink-0" icon={faLanguage} />
			) : (
				<LanguageFlag className="w-4 h-auto shrink-0" language={language} title={triggerTitle} />
			)}
			<span className="truncate text-text">{triggerLabel}</span>
			<Icon
				className={classNames(
					"w-3 h-3 text-text-meta shrink-0 transition-transform",
					showDropdown && "rotate-180"
				)}
				icon={faChevronDown}
			/>
		</button>
	);

	return (
		<>
			{compact ? (
				<Tooltip
					content={t("messages.generateBar.languageSelect.tooltip", { language: triggerTitle })}
				>
					{trigger}
				</Tooltip>
			) : (
				trigger
			)}

			{showDropdown &&
				ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="fixed z-[10000] flex flex-col bg-white border border-gray-200 rounded-md overflow-hidden"
						style={
							portalPosition
								? {
										top: portalPosition.top,
										bottom: portalPosition.bottom,
										left: portalPosition.left,
										width: MENU_WIDTH,
										maxHeight: portalPosition.maxHeight,
									}
								: { visibility: "hidden", width: MENU_WIDTH }
						}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="p-2 border-b border-b-border">
							<input
								autoFocus
								className="input-styles placeholder-placeholder text-ssm font-normal w-full py-2 px-3"
								data-test="language-select-search"
								placeholder={t("messages.generateBar.languageSelect.searchLanguage")}
								type="text"
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
							/>
						</div>

						<div className="min-h-0 flex-1 overflow-y-auto bb-scrollbar-darker">
							{filteredOptions.length === 0 ? (
								<p className="py-2.5 px-3 text-sm italic text-gray-700">{t("basics.noResults")}</p>
							) : (
								filteredOptions.map((option) => (
									<button
										key={`language-option-${option.id}`}
										className="flex flex-row justify-between items-center py-2.5 px-3 text-sm text-left text-gray-900 w-full bg-white hover:bg-gray-100 duration-200 cursor-pointer"
										type="button"
										onClick={() => handleSelectLanguage(option.id)}
									>
										<div className="flex flex-row items-center gap-2">
											<LanguageFlag
												className="w-4 h-auto"
												language={option.id}
												title={option.id === "auto" ? option.title : option.id.toUpperCase()}
											/>
											{option.title}
										</div>
										{option.id === language && (
											<Icon className="w-4 h-4 text-brand-light" icon={faCheck} />
										)}
									</button>
								))
							)}
						</div>

						{language === "auto" && (
							<button
								className="flex flex-row items-center gap-2 py-2.5 px-3 text-sm text-left text-text-meta w-full bg-white hover:bg-gray-100 duration-200 cursor-pointer border-t border-t-border"
								data-test="language-select-configure-rules"
								type="button"
								onClick={() => {
									closeDropdown();
									onOpenRules();
								}}
							>
								<Icon className="w-4 h-4" icon={faGear} />
								{t("messages.generateBar.languageSelect.configureRules")}
							</button>
						)}
					</div>,
					portalContainer ?? document.body
				)}
		</>
	);
};
